import React, { useState, useRef, useEffect } from 'react';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

interface ContextMenuProps {
  children: React.ReactNode;
  items: ContextMenuItem[];
  disabled?: boolean;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ children, items, disabled = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // メニューを表示
  const showMenu = (clientX: number, clientY: number) => {
    if (disabled) return;

    // ビューポート内でのメニュー位置調整
    const menuWidth = 200; // 想定メニュー幅
    const menuHeight = items.length * 40; // 想定メニュー高さ

    let x = clientX;
    let y = clientY;

    // 画面右端チェック
    if (clientX + menuWidth > window.innerWidth) {
      x = clientX - menuWidth;
    }

    // 画面下端チェック
    if (clientY + menuHeight > window.innerHeight) {
      y = clientY - menuHeight;
    }

    setPosition({ x, y });
    setIsVisible(true);
  };

  // メニューを隠す
  const hideMenu = () => {
    setIsVisible(false);
  };

  // 右クリックハンドラー
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showMenu(e.clientX, e.clientY);
  };

  // 長押し開始
  const handleTouchStart = (e: React.TouchEvent) => {
    longPressTimerRef.current = setTimeout(() => {
      const touch = e.touches[0];
      showMenu(touch.clientX, touch.clientY);
    }, 500); // 500ms長押し
  };

  // 長押し終了
  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // メニュー項目クリック
  const handleItemClick = (item: ContextMenuItem) => {
    if (item.disabled) return;
    item.onClick();
    hideMenu();
  };

  // 外部クリックでメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        hideMenu();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  // ESCキーでメニューを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideMenu();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  return (
    <>
      {/* 子要素（右クリック・長押し対象） */}
      <div
        ref={childRef}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchEnd} // 移動時も長押しキャンセル
        className='context-menu-trigger'
      >
        {children}
      </div>

      {/* コンテキストメニュー */}
      {isVisible && (
        <div
          ref={menuRef}
          className='fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[160px]'
          style={{ left: position.x, top: position.y }}
        >
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              className={`
                w-full text-left px-3 py-2 text-sm flex items-center
                hover:bg-gray-100 dark:hover:bg-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
                ${item.className || 'text-gray-700 dark:text-gray-200'}
              `}
            >
              {item.icon && <item.icon className='w-4 h-4 mr-2 flex-shrink-0' />}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};
